import React, { useState, useMemo } from 'react';
import { Box, TextField, Button, Typography, Alert, CircularProgress, Link, LinearProgress } from '@mui/material';
import { authApi } from '../api/services';
import BrandMark from '../components/BrandMark';

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { score: 25, label: 'Weak', color: '#EF5350' };
  if (score <= 3) return { score: 50, label: 'Fair', color: '#FFA726' };
  if (score <= 4) return { score: 75, label: 'Good', color: '#FFA726' };
  return { score: 100, label: 'Strong', color: '#66BB6A' };
}

const defaultBackground = `
  radial-gradient(ellipse at 20% 50%, rgba(124, 58, 237, 0.15) 0%, transparent 50%),
  radial-gradient(ellipse at 80% 20%, rgba(37, 99, 235, 0.12) 0%, transparent 50%),
  radial-gradient(ellipse at 60% 80%, rgba(13, 148, 136, 0.08) 0%, transparent 50%),
  linear-gradient(180deg, #060912 0%, #0A0E17 50%, #0D1220 100%)
`;

const gridOverlay = `
  linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
  linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
`;

export default function PasswordResetPage() {
  const params = new URLSearchParams(window.location.search);
  const emailParam = params.get('email') || '';
  const tokenParam = params.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword({
        email: emailParam,
        token: tokenParam,
        newPassword,
      });
      setSuccess(true);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (Array.isArray(errors)) {
        setError(errors.join(' '));
      } else {
        setError(err.response?.data?.error || 'Password reset failed. The link may have expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        background: defaultBackground,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: gridOverlay,
          backgroundSize: '60px 60px',
          opacity: 1,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Floating accent orb -- top right */}
      <Box sx={{
        position: 'absolute', top: -120, right: -80,
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)',
        filter: 'blur(40px)', pointerEvents: 'none',
      }} />

      {/* Floating accent orb -- bottom left */}
      <Box sx={{
        position: 'absolute', bottom: -100, left: -60,
        width: 350, height: 350, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)',
        filter: 'blur(40px)', pointerEvents: 'none',
      }} />

      {/* Card */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 420,
          width: '100%',
          mx: 2,
          p: 5,
          borderRadius: 3,
          background: 'rgba(18, 24, 41, 0.85)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 80px rgba(124,58,237,0.05)',
        }}
      >
        {/* Logo and brand */}
        <Box mb={1}>
          <BrandMark size="md" showStratiumLine accent="light" />
        </Box>

        <Typography variant="body2" color="text.secondary" mb={4} sx={{ fontSize: 13, letterSpacing: '0.02em' }}>
          Set your password
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              Password reset successfully. You can now sign in with your new password.
            </Alert>
            <Button
              variant="contained"
              fullWidth
              size="large"
              href="/login"
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: 15,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #7C3AED 0%, #6C3ACD 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)' },
                boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
              }}
            >
              Go to Sign In
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {emailParam && (
              <Typography variant="body2" color="text.secondary" mb={2.5} sx={{ fontSize: 13 }}>
                Setting password for <strong style={{ color: '#E8ECF4' }}>{emailParam}</strong>
              </Typography>
            )}

            <TextField
              label="New Password"
              type="password"
              fullWidth
              required
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              sx={{ mb: 0.5 }}
              autoFocus
              variant="outlined"
              InputProps={{
                sx: { borderRadius: 2, background: 'rgba(255,255,255,0.03)' }
              }}
              inputProps={{ minLength: 8 }}
            />
            {newPassword && (
              <Box sx={{ mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={strength.score}
                    sx={{
                      flex: 1, height: 4, borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.06)',
                      '& .MuiLinearProgress-bar': { bgcolor: strength.color, borderRadius: 2 },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: strength.color, fontSize: 11, minWidth: 40 }}>
                    {strength.label}
                  </Typography>
                </Box>
              </Box>
            )}
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(136,146,168,0.6)', fontSize: 11, mb: 2 }}>
              Min 8 chars, uppercase, lowercase, number, special character
            </Typography>
            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              required
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              sx={{ mb: 0.5 }}
              variant="outlined"
              InputProps={{
                sx: { borderRadius: 2, background: 'rgba(255,255,255,0.03)' }
              }}
            />
            {confirmPassword && (
              <Typography variant="caption" sx={{
                display: 'block', fontSize: 11, mb: 2,
                color: passwordsMatch ? '#66BB6A' : '#EF5350',
              }}>
                {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
              </Typography>
            )}
            {!confirmPassword && <Box sx={{ mb: 3 }} />}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: 15,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #7C3AED 0%, #6C3ACD 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)' },
                boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Reset Password'}
            </Button>
          </form>
        )}

        {/* Footer */}
        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            <Link href="/login" underline="hover" sx={{ color: '#7C3AED', fontWeight: 600 }}>
              Back to sign in
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
